import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import { isErrorUserPendingApproval, isForbiddenError, storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  daysBetween,
  getStartOf,
} from '../../util/dates';
import { constructQueryParamName, isOriginInUse, isStockInUse } from '../../util/search';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { parse } from '../../util/urlHelpers';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { searchAndTransformEvents } from '../../util/ticketmasterApi';
import { searchAndTransformSeatGeekEvents } from '../../util/seatgeekSearchApi';

// Destructure UUID from sdkTypes
const { UUID } = sdkTypes;

const GENERIC_EXTERNAL_SEARCH_KEYWORDS = new Set(['ticket bids', 'tickets', 'events']);
const shouldSkipGenericExternalSearch = keyword =>
  GENERIC_EXTERNAL_SEARCH_KEYWORDS.has(String(keyword || '').trim().toLowerCase());

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 24;

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

// Combined search with Ticketmaster integration
export const SEARCH_COMBINED_REQUEST = 'app/SearchPage/SEARCH_COMBINED_REQUEST';
export const SEARCH_COMBINED_SUCCESS = 'app/SearchPage/SEARCH_COMBINED_SUCCESS';
export const SEARCH_COMBINED_ERROR = 'app/SearchPage/SEARCH_COMBINED_ERROR';

export const SEARCH_TICKETMASTER_REQUEST = 'app/SearchPage/SEARCH_TICKETMASTER_REQUEST';
export const SEARCH_TICKETMASTER_SUCCESS = 'app/SearchPage/SEARCH_TICKETMASTER_SUCCESS';
export const SEARCH_TICKETMASTER_ERROR = 'app/SearchPage/SEARCH_TICKETMASTER_ERROR';

export const SEARCH_SEATGEEK_REQUEST = 'app/SearchPage/SEARCH_SEATGEEK_REQUEST';
export const SEARCH_SEATGEEK_SUCCESS = 'app/SearchPage/SEARCH_SEATGEEK_SUCCESS';
export const SEARCH_SEATGEEK_ERROR = 'app/SearchPage/SEARCH_SEATGEEK_ERROR';

export const CLEAR_SEARCH_RESULTS = 'app/SearchPage/CLEAR_SEARCH_RESULTS';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  // Map-related state
  searchMapListingIds: [],
  searchMapListingsError: null,
  activeListingId: null,
  // Combined search state
  ticketmasterSearchInProgress: false,
  ticketmasterSearchError: null,
  ticketmasterResultIds: [],
  seatgeekSearchInProgress: false,
  seatgeekSearchError: null,
  seatgeekResultIds: [],
  combinedSearchInProgress: false,
};

const resultIds = data => {
  const listings = data.data;
  return listings
    .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
    .map(l => l.id);
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
      };
    case SEARCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case SEARCH_MAP_LISTINGS_REQUEST:
      return {
        ...state,
        searchMapListingIds: [],
        searchMapListingsError: null,
      };

    case SEARCH_MAP_LISTINGS_SUCCESS:
      return {
        ...state,
        searchMapListingIds: resultIds(payload.data),
      };

    case SEARCH_MAP_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        searchMapListingsError: payload,
      };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };

    // Combined search cases
    case SEARCH_COMBINED_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        combinedSearchInProgress: true,
        searchInProgress: true,
        searchListingsError: null,
        ticketmasterSearchError: null,
        seatgeekSearchError: null,
      };

    case SEARCH_COMBINED_SUCCESS:
      const { localListings, ticketmasterEvents, seatgeekEvents } = payload;
      const combinedIds = [
        ...(state.currentPageResultIds || []),
        ...(ticketmasterEvents || []),
        ...(seatgeekEvents || []),
      ];
      return {
        ...state,
        currentPageResultIds: combinedIds,
        ticketmasterResultIds: ticketmasterEvents || [],
        seatgeekResultIds: seatgeekEvents || [],
        combinedSearchInProgress: false,
        searchInProgress: false,
      };

    case SEARCH_COMBINED_ERROR:
      return {
        ...state,
        combinedSearchInProgress: false,
        searchInProgress: false,
        searchListingsError: payload,
      };

    case SEARCH_TICKETMASTER_REQUEST:
      return {
        ...state,
        ticketmasterSearchInProgress: true,
        ticketmasterSearchError: null,
      };

    case SEARCH_TICKETMASTER_SUCCESS:
      return {
        ...state,
        ticketmasterSearchInProgress: false,
        ticketmasterResultIds: payload.events?.map(e => e.id) || [],
      };

    case SEARCH_TICKETMASTER_ERROR:
      return {
        ...state,
        ticketmasterSearchInProgress: false,
        ticketmasterSearchError: payload,
      };

    case SEARCH_SEATGEEK_REQUEST:
      return {
        ...state,
        seatgeekSearchInProgress: true,
        seatgeekSearchError: null,
      };

    case SEARCH_SEATGEEK_SUCCESS:
      return {
        ...state,
        seatgeekSearchInProgress: false,
        seatgeekResultIds: payload.events?.map(e => e.id) || [],
      };

    case SEARCH_SEATGEEK_ERROR:
      return {
        ...state,
        seatgeekSearchInProgress: false,
        seatgeekSearchError: payload,
      };

    case CLEAR_SEARCH_RESULTS:
      return {
        ...state,
        currentPageResultIds: [],
        ticketmasterResultIds: [],
        seatgeekResultIds: [],
        searchParams: null,
        searchInProgress: false,
        searchListingsError: null,
        ticketmasterSearchInProgress: false,
        ticketmasterSearchError: null,
        seatgeekSearchInProgress: false,
        seatgeekSearchError: null,
        combinedSearchInProgress: false,
      };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = response => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// Map search action creators
export const searchMapListingsRequest = searchParams => ({
  type: SEARCH_MAP_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchMapListingsSuccess = response => ({
  type: SEARCH_MAP_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchMapListingsError = e => ({
  type: SEARCH_MAP_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// Combined search action creators
export const searchCombinedRequest = searchParams => ({
  type: SEARCH_COMBINED_REQUEST,
  payload: { searchParams },
});

export const searchCombinedSuccess = (localListings, ticketmasterEvents, seatgeekEvents) => ({
  type: SEARCH_COMBINED_SUCCESS,
  payload: { localListings, ticketmasterEvents, seatgeekEvents },
});

export const searchCombinedError = error => ({
  type: SEARCH_COMBINED_ERROR,
  error: true,
  payload: error,
});

export const clearSearchResults = () => ({
  type: CLEAR_SEARCH_RESULTS,
});

export const searchTicketmasterRequest = searchParams => ({
  type: SEARCH_TICKETMASTER_REQUEST,
  payload: { searchParams },
});

export const searchTicketmasterSuccess = events => ({
  type: SEARCH_TICKETMASTER_SUCCESS,
  payload: { events },
});

export const searchTicketmasterError = error => ({
  type: SEARCH_TICKETMASTER_ERROR,
  error: true,
  payload: error,
});

export const searchSeatgeekRequest = searchParams => ({
  type: SEARCH_SEATGEEK_REQUEST,
  payload: { searchParams },
});

export const searchSeatgeekSuccess = events => ({
  type: SEARCH_SEATGEEK_SUCCESS,
  payload: { events },
});

export const searchSeatgeekError = error => ({
  type: SEARCH_SEATGEEK_ERROR,
  error: true,
  payload: error,
});

export const searchListings = (searchParams, config) => (dispatch, getState, sdk) => {
  dispatch(searchListingsRequest(searchParams));

  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = listingTypes => {
    // Add error handling for missing config structure
    const enforceValidListingType = config?.listing?.enforceValidListingType;
    return enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
          // Add error handling for missing config structure
          pub_transactionProcessAlias: listingTypes.map(l => l.transactionType?.alias),
          // Add error handling for missing config structure
          pub_unitType: listingTypes.map(l => l.transactionType?.unitType),
        }
      : {};
  };

  const omitInvalidCategoryParams = params => {
    // Add null check for config structure
    if (
      !config?.categoryConfiguration?.categories ||
      !Array.isArray(config.categoryConfiguration.categories)
    ) {
      return params; // Return original params if category config is invalid
    }

    const categoryConfig = config.search.defaultFilters?.find(f => f.schemaType === 'category');
    const categories = config.categoryConfiguration.categories;
    const { key: prefix, scope } = categoryConfig || {};
    const categoryParamPrefix = constructQueryParamName(prefix, scope);

    const validURLParamForCategoryData = (prefix, categories, level, params) => {
      const levelKey = `${categoryParamPrefix}${level}`;
      const levelValue = params?.[levelKey];
      const foundCategory = categories.find(cat => cat.id === levelValue);
      const subcategories = foundCategory?.subcategories || [];
      return foundCategory && subcategories.length > 0
        ? {
            [levelKey]: levelValue,
            ...validURLParamForCategoryData(prefix, subcategories, level + 1, params),
          }
        : foundCategory
        ? { [levelKey]: levelValue }
        : {};
    };

    const categoryKeys = validURLParamForCategoryData(prefix, categories, 1, params);
    const nonCategoryKeys = Object.entries(params).reduce(
      (picked, [k, v]) => (k.startsWith(categoryParamPrefix) ? picked : { ...picked, [k]: v }),
      {}
    );

    return { ...nonCategoryKeys, ...categoryKeys };
  };

  const priceSearchParams = priceParam => {
    // Add error handling for missing config structure
    const currency = config?.currency || 'USD';
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const datesSearchParams = datesParam => {
    const searchTZ = 'Etc/UTC';
    // Add error handling for missing config structure
    const defaultFilters = config?.search?.defaultFilters || [];
    const datesFilter = defaultFilters.find(f => f.key === 'dates');
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesFilter && datesParam && values.length === 2;
    const { dateRangeMode, availability } = datesFilter || {};
    const isNightlyMode = dateRangeMode === 'night';
    const isEntireRangeAvailable = availability === 'time-full';

    // SearchPage need to use a single time zone but listings can have different time zones
    // We need to expand/prolong the time window (start & end) to cover other time zones too.
    //
    // NOTE: you might want to consider changing UI so that
    //   1) location is always asked first before date range
    //   2) use some 3rd party service to convert location to time zone (IANA tz name)
    //   3) Make exact dates filtering against that specific time zone
    //   This setup would be better for dates filter,
    //   but it enforces a UX where location is always asked first and therefore configurability
    const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
    const getProlongedEnd = date => addTime(date, 12, 'hours', searchTZ);

    const startDate = hasValues ? parseDateFromISO8601(values[0], searchTZ) : null;
    const endRaw = hasValues ? parseDateFromISO8601(values[1], searchTZ) : null;
    const endDate =
      hasValues && isNightlyMode
        ? endRaw
        : hasValues
        ? getExclusiveEndDate(endRaw, searchTZ)
        : null;

    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    const dayCount = isEntireRangeAvailable ? daysBetween(startDate, endDate) : 1;
    const day = 1440;
    const hour = 60;
    // When entire range is required to be available, we count minutes of included date range,
    // but there's a need to subtract one hour due to possibility of daylight saving time.
    // If partial range is needed, then we just make sure that the shortest time unit supported
    // is available within the range.
    // You might want to customize this to match with your time units (e.g. day: 1440 - 60)
    const minDuration = isEntireRangeAvailable ? dayCount * day - hour : hour;
    return hasValidDates
      ? {
          start: getProlongedStart(startDate),
          end: getProlongedEnd(endDate),
          // Availability can be time-full or time-partial.
          // However, due to prolonged time window, we need to use time-partial.
          availability: 'time-partial',
          // minDuration uses minutes
          minDuration,
        }
      : {};
  };

  const stockFilters = datesMaybe => {
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // If dates filter is not in use,
    //   1) Add minStock filter with default value (1)
    //   2) Add relaxed stockMode: "match-undefined"
    // The latter is used to filter out all the listings that explicitly are out of stock,
    // but keeps bookable and inquiry listings.
    return hasDatesFilterInUse ? {} : { minStock: 1, stockMode: 'match-undefined' };
  };

  const seatsSearchParams = (seats, datesMaybe) => {
    // Add error handling for missing config structure
    const defaultFilters = config?.search?.defaultFilters || [];
    const seatsFilter = defaultFilters.find(f => f.key === 'seats');
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;

    // Seats filter cannot be applied without dates
    return hasDatesFilterInUse && seatsFilter ? { seats } : {};
  };

  const { perPage, price, dates, seats, sort, mapSearch, ...restOfParams } = searchParams;
  delete restOfParams.ticketmasterAttractionId;
  delete restOfParams.ticketmasterVenueId;
  const priceMaybe = priceSearchParams(price);
  const datesMaybe = datesSearchParams(dates);
  const stockMaybe = stockFilters(datesMaybe);
  const seatsMaybe = seatsSearchParams(seats, datesMaybe);
  // Add error handling for missing sortConfig structure
  const relevanceKey = config?.search?.sortConfig?.relevanceKey;
  const sortMaybe = sort === relevanceKey ? {} : { sort };

  const params = {
    // The rest of the params except invalid nested category-related params
    // Note: invalid independent search params are still passed through
    ...omitInvalidCategoryParams(restOfParams),
    ...priceMaybe,
    ...datesMaybe,
    ...stockMaybe,
    ...seatsMaybe,
    ...sortMaybe,
    // Add error handling for missing config structure
    ...searchValidListingTypes(config?.listing?.listingTypes || []),
    perPage,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(searchListingsSuccess(response));
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(searchListingsError(error));
      if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
        throw e;
      }
    });
};

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // In private marketplace mode, this page won't fetch data if the user is unauthorized
  const state = getState();
  const currentUser = state.user?.currentUser;
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const hasViewingRights = currentUser && hasPermissionToViewData(currentUser);
  // Add error handling for missing config structure
  const isPrivateMarketplace = config?.accessControl?.marketplace?.private === true;
  const canFetchData =
    !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized && hasViewingRights);
  if (!canFetchData) {
    return Promise.resolve();
  }

  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const { page = 1, address, origin, ...rest } = queryParams;
  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const searchListingsCall = searchListings(
    {
      ...rest,
      ...originMaybe,
      page,
      perPage: RESULT_PAGE_SIZE,
      include: ['author', 'images'],
      'fields.listing': [
        'title',
        'geolocation',
        'price',
        'deleted',
        'state',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        // These help rendering of 'purchase' listings,
        // when transitioning from search page to listing page
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
        'publicData.venueName',
        'publicData.eventDateTime',
        'publicData.venueLocation',
        'publicData.seatRow',
        'publicData.seatNumber',
        'publicData.seatSection',
        'publicData.additionalInfo',
        'publicData.city',
        'publicData.state',
      ],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    },
    config
  );
  return dispatch(searchListingsCall);
};

// ================ Combined Search with Ticketmaster ================ //

const addExternalEventEntities = (dispatch, events) => {
  const validEvents = (events || []).filter(event => event?.id && event?.type);
  if (!validEvents.length) return;

  const imageEntities = [];
  validEvents.forEach(event => {
    const source = event.attributes?.publicData?.source || 'external';
    (event.images || []).forEach(img => {
      imageEntities.push({
        id: new UUID(`${event.id}-img-${img.width || 'default'}x${img.height || 'default'}`),
        type: 'image',
        attributes: {
          variants: {
            'listing-card': {
              name: `${source}-${event.id}-card`,
              url: img.url,
              width: img.width || 300,
              height: img.height || 200,
            },
            'listing-card-2x': {
              name: `${source}-${event.id}-card-2x`,
              url: img.url,
              width: (img.width || 300) * 2,
              height: (img.height || 200) * 2,
            },
            'scaled-medium': {
              name: `${source}-${event.id}-scaled-medium`,
              url: img.url,
              width: img.width || 300,
              height: img.height || 200,
            },
            'scaled-small': {
              name: `${source}-${event.id}-scaled-small`,
              url: img.url,
              width: Math.round((img.width || 300) * 0.75),
              height: Math.round((img.height || 200) * 0.75),
            },
          },
        },
      });
    });
  });

  dispatch(
    addMarketplaceEntities({
      data: {
        data: validEvents,
        included: imageEntities,
      },
    })
  );
};

/**
 * Combined search that queries local listings, Ticketmaster events, and SeatGeek events
 * @param {Object} searchParams - Search parameters including keyword
 * @param {Object} config - Application configuration
 * @returns {Function} Redux thunk function
 */
export const searchCombined = (searchParams, config) => async (dispatch, getState, sdk) => {
  // Dispatch combined search request
  dispatch(searchCombinedRequest(searchParams));

  try {
    // Extract search keyword from params - check multiple possible parameter names
    const keyword =
      searchParams.keywords || searchParams.keyword || searchParams.q || searchParams.search || '';
    const ticketmasterAttractionId = searchParams.ticketmasterAttractionId || '';
    const ticketmasterVenueId = searchParams.ticketmasterVenueId || '';

    // Start all three searches in parallel
    const promises = [];

    // 1. Search local listings (existing functionality)
    const localSearchPromise = dispatch(searchListings(searchParams, config));
    promises.push(localSearchPromise);

    const hasUsefulExternalKeyword =
      keyword && keyword.trim() && !shouldSkipGenericExternalSearch(keyword);

    // 2. Search Ticketmaster events (if keyword exists)
    let ticketmasterPromise = Promise.resolve([]);
    if (hasUsefulExternalKeyword || ticketmasterAttractionId || ticketmasterVenueId) {
      // When viewing a specific attraction, fetch the full result set using only the
      // attractionId (no keyword filter) so it matches the dropdown count endpoint,
      // which also counts by attractionId alone. TM caps page size at 200.
      const isAttractionSearch = !!ticketmasterAttractionId;
      ticketmasterPromise = searchAndTransformEvents(isAttractionSearch ? '' : keyword, {
        size: isAttractionSearch ? 200 : 60,
        page: 0,
        ...(ticketmasterAttractionId ? { attractionId: ticketmasterAttractionId } : {}),
        ...(ticketmasterVenueId ? { ticketmasterVenueId } : {}),
      })
        .then(events => {
          addExternalEventEntities(dispatch, events);
          dispatch(searchTicketmasterSuccess(events));
          return events;
        })
        .catch(error => {
          console.error('🎫 TICKETMASTER SEARCH FAILED:', error);
          // Ticketmaster search failed
          dispatch(searchTicketmasterError(error));
          return []; // Return empty array on error, don't fail the entire search
        });
    } else {
      // No keyword provided; skip Ticketmaster search
    }

    promises.push(ticketmasterPromise);

    // 3. Search SeatGeek events (if keyword exists)
    let seatgeekPromise = Promise.resolve([]);
    if (hasUsefulExternalKeyword) {
      seatgeekPromise = searchAndTransformSeatGeekEvents(keyword, {
        size: 8, // Limit SeatGeek results
        page: 1,
      })
        .then(events => {
          addExternalEventEntities(dispatch, events);
          dispatch(searchSeatgeekSuccess(events));
          return events;
        })
        .catch(error => {
          console.error('🎪 SEATGEEK SEARCH FAILED:', error);
          // SeatGeek search failed
          dispatch(searchSeatgeekError(error));
          return []; // Return empty array on error, don't fail the entire search
        });
    }

    promises.push(seatgeekPromise);

    // Wait for all three searches to complete
    const [localResult, ticketmasterEvents, seatgeekEvents] = await Promise.all(promises);

    // Combine all external events (Ticketmaster + SeatGeek)
    const allExternalEvents = [...(ticketmasterEvents || []), ...(seatgeekEvents || [])];

    // Add external events to marketplace entities if they exist
    if (allExternalEvents && allExternalEvents.length > 0) {
      // Update current page results to include all external events
      const state = getState();
      const currentLocalIds = state.SearchPage?.currentPageResultIds || [];
      const ticketmasterIds = ticketmasterEvents?.map(event => event.id) || [];
      const seatgeekIds = seatgeekEvents?.map(event => event.id) || [];

      // Dispatch success with combined results from all three sources
      dispatch(searchCombinedSuccess(currentLocalIds, ticketmasterIds, seatgeekIds));
    } else {
      // Get current local result IDs
      const state = getState();
      const currentLocalIds = state.SearchPage?.currentPageResultIds || [];
      dispatch(searchCombinedSuccess(currentLocalIds, [], []));
    }
  } catch (error) {
    // Combined search failed
    dispatch(searchCombinedError(error));
  }
};

/**
 * Enhanced search listings that includes Ticketmaster integration
 * This replaces the original searchListings function for hero search
 * @param {Object} searchParams - Search parameters
 * @param {Object} config - Application configuration
 * @returns {Function} Redux thunk function
 */
export const searchListingsWithTicketmaster = (searchParams, config) => {
  return searchCombined(searchParams, config);
};
